import { useCallback } from 'react';

import {
	type AnalyticsEventPayload,
	isUIAnalyticsEvent,
	type UIAnalyticsEvent,
	useAnalyticsEvents,
} from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { type AIEventBaseAttributes, useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useAnalyticsPropertiesContext } from './analytics-properties-context';
import { AI_MATE_ATTRIBUTES, type AIMateAttributeType } from './analytics-properties-context/types';
import { ANALYTICS_CHANNEL } from './constants';
import {
	type AIMAUEventPayload,
	type IUseAnalytics,
	type SendAnalyticsEvent,
	type TrackAIEventPayloads,
} from './types';
import { getAttributesFromContexts } from './utils';

const isTownSquareEvent = () =>
	['home.atlassian.com', 'team.atlassian.com'].some((host) => window.location?.host.includes(host));

const DEFAULT_TAGS: string[] = ['atlaskit', 'aiMate'];

/**
 * A wrapper around the analytics-next hook to send analytics events to the ai mate channel
 */
export const useAnalytics = (): IUseAnalytics => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { propertiesRef, mode } = useAnalyticsPropertiesContext();

	// TODO: Improve this functionality, potentially with a higher order provider for tags or similar
	// This is a temporary solution to add the 'townsquare' tag to operational events so Experience Tracker works in Atlas
	const maybeAtlasOperationalTags = useCallback(
		(inputPayload: AnalyticsEventPayload | UIAnalyticsEvent) => {
			const payload = isUIAnalyticsEvent(inputPayload) ? inputPayload.payload : inputPayload;
			if (
				payload.actionSubject === 'ui' &&
				payload.eventType === 'operational' &&
				isTownSquareEvent()
			) {
				return ['townsquare'];
			}
			return [];
		},
		[],
	);

	const tagsForAIAnalytics = [...DEFAULT_TAGS];
	if (isTownSquareEvent()) {
		tagsForAIAnalytics.push('townsquare');
	}
	const {
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultError,
		trackAIInteractionDismiss,
		trackAIResultAction,
		trackAIFeedbackSubmit,
	} = useAIEventsInstrumentation({
		// Required configruation
		product: propertiesRef.current.product,
		subproduct: propertiesRef.current.product,
		aiFeatureName: 'aiChat',
		proactiveGeneratedAI: 0,
		userGeneratedAI: 1,
		enablePlatformEvents: true,
		tags: tagsForAIAnalytics,
		customAnalyticsFire: (rawEvent, _createdEvent) => {
			// Build event and apply context attributes
			const event = createAnalyticsEvent(rawEvent);
			const aiMateContextAttributes = getAttributesFromContexts(event.context);

			// Fire
			event
				.update({
					attributes: {
						...event.payload.attributes,
						...aiMateContextAttributes,
					},
				})
				.fire(ANALYTICS_CHANNEL);
		},
	});

	const getDefaultAttributes = useCallback((): AIMateAttributeType => {
		const {
			agentCreatorType,
			agentId,
			agentVisibility,
			agentExternalConfigReference,
			agentActions,
			agentIsDefault,
			conversationChannelId,
			experienceId,
			fullScreen,
			product,
			productHeader,
			mode,
		} = propertiesRef.current;
		return {
			[AI_MATE_ATTRIBUTES.agentCreatorType]: agentCreatorType,
			[AI_MATE_ATTRIBUTES.agentId]: agentId,
			[AI_MATE_ATTRIBUTES.agentVisibility]: agentVisibility,
			[AI_MATE_ATTRIBUTES.conversationChannelId]: conversationChannelId,
			[AI_MATE_ATTRIBUTES.experienceId]: experienceId,
			[AI_MATE_ATTRIBUTES.fullScreen]: fullScreen,
			[AI_MATE_ATTRIBUTES.product]: product,
			[AI_MATE_ATTRIBUTES.productHeader]: productHeader,
			[AI_MATE_ATTRIBUTES.singleInstrumentationID]: conversationChannelId,
			[AI_MATE_ATTRIBUTES.agentExternalConfigReference]: agentExternalConfigReference,
			[AI_MATE_ATTRIBUTES.agentActions]: agentActions,
			[AI_MATE_ATTRIBUTES.agentIsDefault]: agentIsDefault,
			[AI_MATE_ATTRIBUTES.mode]: mode,
		};
	}, [propertiesRef]);

	const trackAIMAUEvent = useCallback(
		(payload: TrackAIEventPayloads) => {
			// Handle tags
			const tags = [...DEFAULT_TAGS];
			if (isTownSquareEvent()) {
				tags.push('townsquare');
			}

			const {
				source,
				subjectId,
				proactive,
				userGenerated,
				messageId,
				messageActions,
				singleInstrumentationID,
				deepResearchEnabled,
				webSearchEnabled,
				timestamp,
				citedProductList,
				citedProduct,
				sourceProducts,
				sourceProduct,
				invokedPlugins,
				responseSources,
				usesJsm,
				invokedFrom,
				conversationChannelId,
				attributes: customAttributes,
			} = payload;

			let attributes: AIMAUEventPayload['attributes'] = {
				...getDefaultAttributes(),
				isAIFeature: 1,
				proactiveAIGenerated: proactive ? 1 : 0,
				userGeneratedAI: userGenerated ? 1 : 0,
				aiFeatureName: 'aiChat',
				citedProductList,
				citedProduct,
				sourceProducts,
				sourceProduct,
				...customAttributes,
			};

			if (messageId) {
				attributes.messageId = messageId;
			}

			if (messageActions) {
				attributes.messageActions = messageActions;
			}

			if (invokedFrom) {
				attributes.invokedFrom = invokedFrom;
			}

			if (timestamp) {
				attributes.timestamp = timestamp;
			}

			if (singleInstrumentationID) {
				attributes.singleInstrumentationID = singleInstrumentationID;
			}

			if (invokedPlugins) {
				attributes.invokedPlugins = invokedPlugins;
			}

			if (responseSources) {
				attributes.responseSources = responseSources;
			}

			if (usesJsm !== undefined) {
				attributes.usesJsm = usesJsm;
			}

			if (deepResearchEnabled) {
				attributes.deepResearchEnabled = deepResearchEnabled;
			}

			if (webSearchEnabled) {
				attributes.webSearchEnabled = webSearchEnabled;
			}

			if (conversationChannelId) {
				attributes.conversationChannelId = conversationChannelId;
			}

			// Core AI MAU payload
			let eventPayload: Omit<AIMAUEventPayload, 'actionSubject' | 'action'> | AIMAUEventPayload = {
				eventType: 'track',
				tags,
				source,
				actionSubjectId: subjectId,
				attributes,
			};

			const migrationEnabled = fg('rovo_ai_events_tracking_migration');
			if (migrationEnabled) {
				let libAttributes: AIEventBaseAttributes = {
					...attributes,
					aiFeatureName: 'aiChat',
					proactiveGeneratedAI: proactive ? 1 : 0,
					userGeneratedAI: userGenerated ? 1 : 0,
				};
				switch (payload.type) {
					case 'initiated':
						trackAIInteractionInit({
							attributes: libAttributes,
							tags,
							source,
							actionSubjectId: subjectId,
						});
						break;
					case 'viewed':
						trackAIResultView({
							attributes: libAttributes,
							tags,
							source,
							actionSubjectId: subjectId,
						});
						break;
					case 'error':
						trackAIResultError(
							{ aiErrorMessage: payload.errorMessage },
							{
								attributes: libAttributes,
								tags,
								source,
								actionSubjectId: subjectId,
							},
						);
						break;
					case 'dismissed':
						trackAIInteractionDismiss({
							attributes: libAttributes,
							tags,
							source,
							actionSubjectId: subjectId,
						});
						break;
					case 'actioned':
						trackAIResultAction(payload.actionResult, {
							attributes: libAttributes,
							tags,
							source,
							actionSubjectId: subjectId,
						});
						break;
					case 'feedback':
						trackAIFeedbackSubmit(payload.feedbackResult, {
							attributes: libAttributes,
							tags,
							source,
							actionSubjectId: subjectId,
						});
						break;
				}
			} else {
				switch (payload.type) {
					case 'initiated':
						eventPayload = {
							...eventPayload,
							actionSubject: 'aiInteraction',
							action: 'initiated',
						};
						break;
					case 'viewed':
						eventPayload = {
							...eventPayload,
							actionSubject: 'aiResult',
							action: 'viewed',
						};
						break;
					case 'error':
						eventPayload = {
							...eventPayload,
							actionSubject: 'aiResult',
							action: 'error',
						};
						break;
					case 'dismissed':
						eventPayload = {
							...eventPayload,
							actionSubject: 'aiInteraction',
							action: 'dismissed',
						};
						break;
					case 'actioned':
						eventPayload = {
							...eventPayload,
							actionSubject: 'aiResult',
							action: 'actioned',
							attributes: {
								...eventPayload.attributes,
								aiResultAction: payload.actionResult,
							},
						};
						break;
					case 'feedback':
						eventPayload = {
							...eventPayload,
							actionSubject: 'aiFeedback',
							action: 'submitted',
							attributes: {
								...eventPayload.attributes,
								aiFeedbackResult: payload.feedbackResult,
							},
						};
						break;
				}

				// Build event and apply context attributes
				const event = createAnalyticsEvent(eventPayload);
				const aiMateContextAttributes = getAttributesFromContexts(event.context);

				// Fire
				event
					.update({
						attributes: {
							...event.payload.attributes,
							...aiMateContextAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			}
			// Handle specific AI MAU events
		},
		[
			createAnalyticsEvent,
			getDefaultAttributes,
			trackAIInteractionInit,
			trackAIResultView,
			trackAIResultError,
			trackAIInteractionDismiss,
			trackAIResultAction,
			trackAIFeedbackSubmit,
		],
	);

	const sendAnalyticsEvent: SendAnalyticsEvent = useCallback(
		(payload, options) => {
			const extraTags = options?.removeDefaultTags ? [] : DEFAULT_TAGS;
			const allTags = Array.from(
				new Set([
					...extraTags,
					...((payload as any).tags || []),
					...maybeAtlasOperationalTags(payload),
				]),
			);

			const defaultAttributes = getDefaultAttributes();

			if (isUIAnalyticsEvent(payload)) {
				const aiMateContextAttributes = getAttributesFromContexts(payload.context);

				payload
					.update({
						...payload.payload,
						tags: allTags,
						attributes: {
							...defaultAttributes,
							...payload.payload.attributes,
							...aiMateContextAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			} else {
				const event = createAnalyticsEvent({
					...payload,
					tags: allTags,
					attributes: {
						...defaultAttributes,
						...payload.attributes,
					},
				});

				const aiMateContextAttributes = getAttributesFromContexts(event.context);
				event
					.update({
						attributes: {
							...event.payload.attributes,
							...aiMateContextAttributes,
						},
					})
					.fire(ANALYTICS_CHANNEL);
			}
		},
		[createAnalyticsEvent, maybeAtlasOperationalTags, getDefaultAttributes],
	);

	return {
		sendAnalyticsEvent,
		trackAIMAUEvent,
		mode,
	};
};
